// @flow

import * as React from 'react';
import {useMemo, useState} from 'react';
import Box from 'sail/Box';
import {ButtonLink} from 'sail/Button';
import Card from 'sail/Card';
import {ContentHeader} from 'sail/Content';
import Group from 'sail/Group';
import DataTable, {
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableRow,
} from 'sail/DataTable';
import {MenuItem, OverflowMenu} from 'sail/Menu';
import Switch from 'sail/Switch';
import {BodyMono} from 'sail/Text';
import TruncatedText from 'sail/TruncatedText';
import {Callout} from 'docs/components';
import {useAccount} from 'docs/data';
import {createStripeInternalUrl} from 'docs/util';
import type {DevboxErrorContext, DevboxErrorContextTraceItem} from 'docs/types';
import './DevboxError.scss';

type CallsiteProps = {|
  callsite: string,
|};

const Callsite = ({callsite}: CallsiteProps) => <BodyMono>{callsite}</BodyMono>;

type TraceItemRowProps = {|
  item: DevboxErrorContextTraceItem,
  depth: number,
|};

const GemTraceItemRow = ({item, depth}: TraceItemRowProps) => {
  return (
    <DataTableRow background="offset">
      <DataTableCell align="right" vAlign="middle" width={50} emphasized>
        {depth}
      </DataTableCell>
      <DataTableCell vAlign="middle" width={300}>
        <Callsite callsite={item.callsite} />
      </DataTableCell>
      <DataTableCell vAlign="middle" width="maximized">
        <TruncatedText>
          {item.filename}:{item.line}
        </TruncatedText>
      </DataTableCell>
      <DataTableCell vAlign="middle" align="right" />
    </DataTableRow>
  );
};

const AppOrLibraryTraceItemRow = ({item, depth}: TraceItemRowProps) => {
  const {account} = useAccount();
  const xray = account.xray;

  if (!xray) {
    return null;
  }

  const githubUrl = `${createStripeInternalUrl('github', {
    path: item.filename,
  })}#L${item.line}`;

  const vscodeUrl = `${createStripeInternalUrl('vscode', {
    username: xray.username,
    root: 'docs/',
    path: item.filename,
  })}:${item.line}`;
  return (
    <DataTableRow>
      <DataTableCell align="right" vAlign="middle" width={50} emphasized>
        {depth}
      </DataTableCell>
      <DataTableCell vAlign="middle" width={300}>
        <Callsite callsite={item.callsite} />
      </DataTableCell>
      <DataTableCell vAlign="middle" width="maximized">
        <ButtonLink
          href={vscodeUrl}
          icon="document"
          label={`${item.filename}:${item.line}`}
        />
      </DataTableCell>
      <DataTableCell vAlign="middle" align="right">
        <OverflowMenu size="small" hoverable>
          <MenuItem href={githubUrl} openInNewWindow>
            Open in GitHub...
          </MenuItem>
          <MenuItem href={vscodeUrl} openInNewWindow>
            Open in Visual Studio Code...
          </MenuItem>
        </OverflowMenu>
      </DataTableCell>
    </DataTableRow>
  );
};

const TraceItemRow = ({item, depth}: TraceItemRowProps) => {
  switch (item.type) {
    case 'gem':
      return <GemTraceItemRow item={item} depth={depth} />;
    default:
      return <AppOrLibraryTraceItemRow item={item} depth={depth} />;
  }
};

type DevboxErrorProps = {|
  error: DevboxErrorContext,
|};

export const DevboxError = ({error}: DevboxErrorProps) => {
  const {account} = useAccount();
  const [showLibraries, setShowLibraries] = useState(true);
  const [showGems, setShowGems] = useState(false);

  const items = useMemo(() => {
    return error.trace.filter((item) => {
      if (item.type === 'gem' && !showGems) {
        return false;
      }
      if (item.type === 'library' && !showLibraries) {
        return false;
      }
      return true;
    });
  }, [error.trace, showGems, showLibraries]);

  if (!account.xray) {
    return null;
  }

  return (
    <Box className="DevboxError">
      <Callout
        title="An error occurred while your devbox was handling this request!"
        type="warning"
      >
        <Box padding={{top: 12}}>
          <BodyMono>{error.message}</BodyMono>
        </Box>
      </Callout>
      <Card shadow="keyline">
        <ContentHeader
          title="Stack trace"
          end={{
            content: (
              <Group direction="horizontal" spacing={24}>
                <Switch
                  label="Libraries"
                  value={showLibraries}
                  onChange={() => setShowLibraries((prev) => !prev)}
                />
                <Switch
                  label="Gems"
                  value={showGems}
                  onChange={() => setShowGems((prev) => !prev)}
                />
              </Group>
            ),
          }}
        />
        <DataTable>
          <DataTableHead
            columns={[
              {label: ''},
              {label: 'Callsite'},
              {label: 'Location'},
              {label: ''},
            ]}
          />
          <DataTableBody>
            {items.map((item, index) => (
              <TraceItemRow key={index} item={item} depth={index} />
            ))}
          </DataTableBody>
        </DataTable>
      </Card>
    </Box>
  );
};
